import React, { useState, useMemo } from "react";
import '../styles/ProductList.css';
import { IP } from '../configuracion.js';

const GroupList = ({ products = [], onSelectProduct, propertyConfig = {}, property }) => {
  // IDs de todos los productos de nota (uno por revenue)
  const openNoteIds = useMemo(() => {
    return new Set((propertyConfig?.opennote || []).map(n => n.Itemprecio));
  }, [propertyConfig]);

  // Filtrar TODOS los productos opennote del catálogo normal
  const filteredProducts = useMemo(() => {
    return products.filter(product => !openNoteIds.has(product.id));
  }, [products, openNoteIds]);

  // Obtener categorías únicas, excluyendo el producto de nota abierta
  const categories = useMemo(() => {
    return [...new Set(filteredProducts
      .filter(product => product.category && typeof product.category === 'string')
      .map(product => product.category)
    )];
  }, [filteredProducts]);

  // Estado para la categoría seleccionada
  const [selectedCategory, setSelectedCategory] = useState(categories[0] || null);
  
  // Estado para el grupo seleccionado
  const [selectedGroup, setSelectedGroup] = useState(null);

  // ── Requerimiento "TOP" (productos más vendidos por categoría/revenue,
  //    contextual al horario actual, zona horaria correcta, top 20) ──
  // Estado nuevo, aislado del resto de la navegación: no toca selectedCategory
  // ni selectedGroup salvo para mostrar/ocultar esta vista.
  const TOP_COUNT = 20;
  const TOP_HOUR_WINDOW = 1; // ± horas alrededor de la hora actual (banda de 3h)
  const DEFAULT_TIMEZONE = 'America/Mexico_City'; // mismo default ya usado en ControlFecha.js

  const [showTop, setShowTop] = useState(false);
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState(null);
  const [topSalesRaw, setTopSalesRaw] = useState(null); // null = aún no se ha consultado
  const [businessTimezone, setBusinessTimezone] = useState(DEFAULT_TIMEZONE);
  const [topReferenceTime, setTopReferenceTime] = useState(null); // "ahora" al momento del clic

  const fetchTopSales = async () => {
    setTopLoading(true);
    setTopError(null);
    try {
      const [ventasResponse, timezoneResponse] = await Promise.all([
        fetch(`${IP}/tavpos/backend/lectura_productos_vendidos.php?property=${encodeURIComponent(property || '')}`, {
          method: 'GET',
          headers: { 'X-Property': property || '', 'Content-Type': 'application/json' },
          mode: 'cors',
        }),
        // No pude confirmar el nombre exacto del campo de zona horaria desde
        // aquí (no hay acceso al backend real en este proyecto local); se
        // intentan variantes razonables y si no viene ninguna se usa el
        // default de arriba, así nunca rompe el flujo si el campo no existe.
        fetch(`${IP}/tavpos/backend/get_timezone.php?property=${encodeURIComponent(property || '')}`).catch(() => null),
      ]);

      if (!ventasResponse.ok) throw new Error(`Error ${ventasResponse.status}`);
      const ventasData = await ventasResponse.json();
      if (!ventasData.success) throw new Error(ventasData.message || 'No se pudo obtener el historial de ventas.');
      setTopSalesRaw(ventasData.data || []);

      if (timezoneResponse && timezoneResponse.ok) {
        const tzData = await timezoneResponse.json();
        const tz = tzData?.data?.timezone || tzData?.data?.zona_horaria || tzData?.data?.timeZone;
        if (tz) setBusinessTimezone(tz);
      }
    } catch (err) {
      console.error('[GroupList/TOP] Error al consultar productos más vendidos:', err);
      setTopError(err.message || 'No se pudo consultar el historial de ventas.');
    } finally {
      setTopLoading(false);
    }
  };

  const handleTopClick = () => {
    setShowTop(true);
    setTopReferenceTime(new Date()); // recalcula "ahora" en cada apertura del TOP
    if (topSalesRaw === null && !topLoading) {
      fetchTopSales();
    }
  };

  // Hora (0-23) de una fecha en la zona horaria del negocio, sin depender
  // de la zona horaria del dispositivo del cajero/mesero.
  const getHourInTimezone = (date, timeZone) => {
    try {
      const hourStr = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', hour12: false }).format(date);
      return parseInt(hourStr, 10) % 24;
    } catch (e) {
      return date.getHours(); // timeZone inválido: mejor esto que romper el TOP
    }
  };

  const isWithinHourWindow = (hour, centerHour, windowHours) => {
    const diff = Math.abs(hour - centerHour);
    return Math.min(diff, 24 - diff) <= windowHours;
  };

  // Cruza el historial de ventas (por nombre) con el catálogo YA cargado de
  // este revenue, para: (a) confiar en product.category del catálogo en vez
  // del campo "departamento" del historial, que puede no calzar 1:1, y
  // (b) recuperar el objeto de producto completo (id, price, stock, etc.)
  // que exige handleProductClick para agregarlo al pedido igual que
  // cualquier otro producto del menú. Además filtra el historial a la banda
  // horaria alrededor de "ahora" (zona horaria del negocio), para que el
  // TOP refleje lo más vendido en ESE horario y no un promedio de todo el día.
  const topProductsForCategory = useMemo(() => {
    if (!topSalesRaw || !selectedCategory || !topReferenceTime) return [];

    const currentHour = getHourInTimezone(topReferenceTime, businessTimezone);

    const cantidadPorNombre = new Map();
    topSalesRaw.forEach((venta) => {
      const nombre = venta.nombre;
      if (!nombre || !venta.fecha_control) return;
      const fechaVenta = new Date(venta.fecha_control);
      if (Number.isNaN(fechaVenta.getTime())) return;
      const horaVenta = getHourInTimezone(fechaVenta, businessTimezone);
      if (!isWithinHourWindow(horaVenta, currentHour, TOP_HOUR_WINDOW)) return;

      const cantidadPrevia = cantidadPorNombre.get(nombre) || 0;
      cantidadPorNombre.set(nombre, cantidadPrevia + (Number(venta.cantidad) || 0));
    });

    // Fallback: si en esta banda horaria no hay historial suficiente
    // (ej. categoría nueva o poco tráfico a esta hora), se usa el
    // histórico completo de la categoría para no dejar el TOP vacío.
    if (cantidadPorNombre.size === 0) {
      topSalesRaw.forEach((venta) => {
        const nombre = venta.nombre;
        if (!nombre) return;
        const cantidadPrevia = cantidadPorNombre.get(nombre) || 0;
        cantidadPorNombre.set(nombre, cantidadPrevia + (Number(venta.cantidad) || 0));
      });
    }

    const productosDeLaCategoria = filteredProducts.filter(
      (product) => product.category === selectedCategory
    );

    return productosDeLaCategoria
      .filter((product) => cantidadPorNombre.has(product.name))
      .map((product) => ({
        ...product,
        _cantidadVendida: cantidadPorNombre.get(product.name),
      }))
      .sort((a, b) => b._cantidadVendida - a._cantidadVendida)
      .slice(0, TOP_COUNT);
  }, [topSalesRaw, selectedCategory, filteredProducts, businessTimezone, topReferenceTime]);

  // Obtener grupos para la categoría seleccionada
  const groupsForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return [...new Set(
      filteredProducts
        .filter(product => product.category === selectedCategory && product.group)
        .map(product => product.group)
    )];
  }, [filteredProducts, selectedCategory]);

  // Obtener productos del grupo seleccionado
  const productsForGroup = useMemo(() => {
    if (!selectedCategory || !selectedGroup) return [];
    return filteredProducts.filter(product => 
      product.category === selectedCategory && product.group === selectedGroup
    );
  }, [filteredProducts, selectedCategory, selectedGroup]);

  // Encontrar el producto de nota que pertenece a ESTE revenue
  // (solo uno de los IDs estará en el catálogo cargado)
  const openNoteProduct = useMemo(() => {
    return products.find(p => openNoteIds.has(p.id)) || null;
  }, [products, openNoteIds]);

  // Función para generar color basado en grupo
  const getGroupColor = (group) => {
    if (!group) return '#6c757d'; // Color por defecto
    const hue = [...group].reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;
    return `hsl(${hue}, 70%, 60%)`;
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedGroup(null);
  };

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
  };

  // Función para manejar el clic en un producto y agregarlo al pedido
  const handleProductClick = (product) => {
    if (product.stock === 0) {
      console.log("🚫 Producto agotado:", product.name);
      return;
    }
    
    console.log("🛒 Producto seleccionado:", product);
    
    if (onSelectProduct && typeof onSelectProduct === 'function') {
      onSelectProduct(product);
    } else {
      console.error("❌ onSelectProduct no es una función válida");
    }
  };

  return (
    <div className="layered-navigation-view">
      {/* Selector de categorías (siempre visible) */}
      <div className="category-selector">
        <div className="category-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              {category}
            </button>
          ))}
          {selectedCategory && (
            <button
              className={`category-tab ${showTop ? 'active' : ''}`}
              onClick={handleTopClick}
              style={!showTop ? { background: 'linear-gradient(145deg, #fff8e6, #ffedc2)', borderColor: '#f2b705' } : undefined}
              title={`Productos más vendidos de ${selectedCategory}`}
            >
              🏆 TOP
            </button>
          )}
        </div>
      </div>

      {/* Contenedor principal que cambia entre grupos y productos */}
      <div className="main-content-area">
        {showTop ? (
          /* Requerimiento "TOP": productos más vendidos de la categoría actual,
             sin tocar la navegación normal de grupos/productos de abajo. */
          <div className="products-view">
            <div className="view-header">
              <button className="back-button" onClick={() => setShowTop(false)}>
                ← Regresar
              </button>
              <h2>🏆 Top {TOP_COUNT} de {selectedCategory}</h2>
              {topReferenceTime && (
                <span style={{ fontSize: '0.75rem', color: '#94734a', marginLeft: '8px' }}>
                  horario {String((getHourInTimezone(topReferenceTime, businessTimezone) - TOP_HOUR_WINDOW + 24) % 24).padStart(2, '0')}:00–
                  {String((getHourInTimezone(topReferenceTime, businessTimezone) + TOP_HOUR_WINDOW + 1) % 24).padStart(2, '0')}:00
                </span>
              )}
            </div>

            {topLoading && <p className="no-items-message">Consultando historial de ventas…</p>}

            {!topLoading && topError && (
              <div className="no-items-message">
                <p>{topError}</p>
                <button className="back-button" onClick={fetchTopSales}>
                  Reintentar
                </button>
              </div>
            )}

            {!topLoading && !topError && topProductsForCategory.length === 0 && (
              <p className="no-items-message">
                Aún no hay historial de ventas suficiente para {selectedCategory}.
              </p>
            )}

            {!topLoading && !topError && topProductsForCategory.length > 0 && (
              <div className="products-grid">
                {topProductsForCategory.map((product, rank) => {
                  const groupColor = getGroupColor(product.group);
                  const textColor = getContrastColor(groupColor);
                  const productInitial = product.name.charAt(0).toUpperCase();

                  return (
                    <div
                      key={product.id}
                      className={`product-item ${product.stock === 0 ? 'disabled' : ''}`}
                      onClick={() => handleProductClick(product)}
                      style={{ position: 'relative' }}
                    >
                      <div style={{
                        position: 'absolute', top: '8px', left: '8px',
                        background: '#f2b705', color: '#4a3600',
                        fontSize: '0.65rem', fontWeight: 800,
                        borderRadius: '10px', padding: '2px 7px',
                      }}>
                        #{rank + 1}
                      </div>
                      <div
                        className="product-initial"
                        style={{ backgroundColor: groupColor, color: textColor }}
                      >
                        {productInitial}
                      </div>
                      <h3>{product.name}</h3>
                      <p>${product.price.toFixed(2)}</p>
                      <div style={{ fontSize: '0.7rem', color: '#94734a', marginTop: '4px' }}>
                        {product._cantidadVendida} vendidos
                      </div>
                      {product.stock !== undefined && (
                        <div
                          className={`stock-indicator ${
                            product.stock > 0 ? 'available' : 'out-of-stock'
                          }`}
                        >
                          {product.stock > 0 ? 'Disponible' : 'Agotado'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : !selectedGroup ? (
          /* Vista de grupos (cuando no hay grupo seleccionado) */
          <div className="groups-view">
            <h2>Grupos de {selectedCategory || '...'}</h2>
            {groupsForCategory.length === 0 && !selectedCategory ? (
              <p className="no-items-message">No hay grupos disponibles.</p>
            ) : groupsForCategory.length === 0 ? (
              <p className="no-items-message">No hay grupos disponibles para {selectedCategory}.</p>
            ) : (
              <div className="groups-grid">
                {groupsForCategory.map((group) => {
                  const groupColor = getGroupColor(group);
                  const textColor = getContrastColor(groupColor);
                  
                  return (
                    <div
                      key={group}
                      className="group-item"
                      onClick={() => handleGroupClick(group)}
                      style={{ backgroundColor: groupColor }}
                    >
                      <div className="group-icon" style={{ backgroundColor: textColor, color: groupColor }}>
                        {group.charAt(0).toUpperCase()}
                      </div>
                      <h3 style={{ color: textColor }}>{group}</h3>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Sección de modificadores siempre visible al final */}
            {openNoteProduct && (
              <div className="opennote-section">
                <div className="products-grid">
                  <div
                    className="small-product-item"
                    onClick={() => handleProductClick(openNoteProduct)}
                  >
                    <h3>{openNoteProduct.name || 'Nota Abierta'}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Vista de productos (cuando hay un grupo seleccionado) */
          <div className="products-view">
            <div className="view-header">
              <button className="back-button" onClick={handleBackToGroups}>
                ← Regresar
              </button>
              <h2>Productos de {selectedGroup}</h2>
            </div>
            
            {productsForGroup.length === 0 ? (
              <p className="no-items-message">No hay productos disponibles en {selectedGroup}.</p>
            ) : (
              <div className="products-grid">
                {productsForGroup.map((product) => {
                  const groupColor = getGroupColor(selectedGroup);
                  const textColor = getContrastColor(groupColor);
                  const productInitial = product.name.charAt(0).toUpperCase();

                  return (
                    <div
                      key={product.id}
                      className={`product-item ${product.stock === 0 ? 'disabled' : ''}`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div
                        className="product-initial"
                        style={{
                          backgroundColor: groupColor,
                          color: textColor,
                        }}
                      >
                        {productInitial}
                      </div>
                      <h3>{product.name}</h3>
                      <p>${product.price.toFixed(2)}</p>
                      {product.stock !== undefined && (
                        <div
                          className={`stock-indicator ${
                            product.stock > 0 ? 'available' : 'out-of-stock'
                          }`}
                        >
                          {product.stock > 0 ? 'Disponible' : 'Agotado'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Sección de modificadores siempre visible al final */}
            {openNoteProduct && (
              <div className="opennote-section">
                <div className="products-grid">
                  <div
                    className="small-product-item"
                    onClick={() => handleProductClick(openNoteProduct)}
                  >
                    <h3>{openNoteProduct.name || 'Nota Abierta'}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Función para determinar color de contraste
const getContrastColor = (bgColor) => {
  if (bgColor.startsWith('hsl')) {
    const values = bgColor.match(/\d+/g);
    const lightness = parseInt(values[2]);
    return lightness > 50 ? '#000000' : '#ffffff';
  }
  return '#000000';
};

export default GroupList;

